import React, { useState, useRef, useEffect } from 'react';
import { X, Mic, Square, Play, Upload, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { Capacitor } from '@capacitor/core';
import { useLanguage } from '../context/LanguageContext';
import { useCT } from '../utils/creationTranslations';
import { useKeyboardHeight } from '../hooks/useKeyboardHeight';
import MonetizationInfoBubble from './MonetizationInfoBubble';

const AudioRecorder = ({ onClose, onSuccess, replyTo }) => {
  const { t } = useLanguage();
  const ct = useCT();
  const keyboardHeight = useKeyboardHeight();
  const [isRecording, setIsRecording] = useState(false);
  const [audioBlob, setAudioBlob] = useState(null);
  const [audioUrl, setAudioUrl] = useState(null);
  const [recordingTime, setRecordingTime] = useState(0);
  const [title, setTitle] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadMode, setUploadMode] = useState('record'); // 'record' or 'upload'
  const [keyboardVisible, setKeyboardVisible] = useState(false);
  const [effects, setEffects] = useState({ fun: [], pro: [] });
  const [selectedEffect, setSelectedEffect] = useState(null); // { id, engine, label, emoji }
  const [previewing, setPreviewing] = useState(false);
  const [originalBlob, setOriginalBlob] = useState(null);
  const [backgroundImage, setBackgroundImage] = useState(null); // File | null
  const [backgroundPreview, setBackgroundPreview] = useState(null); // objectURL
  
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const streamRef = useRef(null);
  const fileInputRef = useRef(null);
  const modalRef = useRef(null);
  const titleInputRef = useRef(null);

  const MAX_RECORDING_TIME = 60; // 60 seconds
  
  // Check if running on iOS
  const isIOS = Capacitor.getPlatform() === 'ios' || /iPhone|iPod/.test(navigator.userAgent);

  useEffect(() => {
    return () => {
      // Cleanup
      if (timerRef.current) clearInterval(timerRef.current);
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
      if (audioUrl) {
        URL.revokeObjectURL(audioUrl);
      }
    };
  }, [audioUrl]);

  // Load audio effect presets when an audio is recorded
  useEffect(() => {
    if (!audioBlob) return;
    if (effects.fun.length > 0) return;
    axios.get('/api/audio-effects/presets')
      .then(res => setEffects(res.data || { fun: [], pro: [] }))
      .catch(() => {});
  }, [audioBlob, effects.fun.length]);

  // Apply an effect (server-side preview)
  const applyEffect = async (preset) => {
    if (!audioBlob) return;
    if (selectedEffect && selectedEffect.id === preset.id) {
      // Toggle off → restore original
      if (originalBlob) {
        const url = URL.createObjectURL(originalBlob);
        setAudioBlob(originalBlob);
        setAudioUrl(url);
      }
      setSelectedEffect(null);
      return;
    }
    setPreviewing(true);
    try {
      const sourceBlob = originalBlob || audioBlob;
      if (!originalBlob) setOriginalBlob(audioBlob);
      const formData = new FormData();
      formData.append('audio_file', sourceBlob, sourceBlob.name || 'recording.webm');
      formData.append('preset_id', preset.id);
      formData.append('engine', preset.engine);
      const resp = await axios.post('/api/audio-effects/preview', formData, {
        responseType: 'blob',
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const newBlob = new Blob([resp.data], { type: 'audio/mpeg' });
      newBlob.name = 'effect.mp3';
      const newUrl = URL.createObjectURL(newBlob);
      setAudioBlob(newBlob);
      setAudioUrl(newUrl);
      setSelectedEffect(preset);
      toast.success(`${preset.emoji || '✨'} ${ct('gim.okEffect', { emoji: preset.emoji || '✨', label: preset.label })}`);
    } catch (e) {
      console.error('effect error', e);
      toast.error(ct('gim.errEffect'));
    } finally {
      setPreviewing(false);
    }
  };

  // Handle iOS keyboard visibility
  useEffect(() => {
    if (!isIOS) return;

    const handleFocusIn = () => {
      setKeyboardVisible(true);
      // Scroll the title input into view after keyboard opens
      setTimeout(() => {
        if (titleInputRef.current) {
          titleInputRef.current.scrollIntoView({ 
            behavior: 'smooth', 
            block: 'center' 
          });
        }
      }, 350);
    };

    const handleFocusOut = () => {
      setTimeout(() => {
        setKeyboardVisible(false);
      }, 100);
    };

    const titleInput = titleInputRef.current;
    if (titleInput) {
      titleInput.addEventListener('focus', handleFocusIn);
      titleInput.addEventListener('blur', handleFocusOut);
    }

    return () => {
      if (titleInput) {
        titleInput.removeEventListener('focus', handleFocusIn);
        titleInput.removeEventListener('blur', handleFocusOut);
      }
    };
  }, [isIOS, audioBlob]); // Re-attach when audioBlob changes (input appears)

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          sampleRate: 44100
        }
      });
      streamRef.current = stream;
      
      // Try different formats in order of preference
      // Safari/iOS uses mp4/aac, Chrome/Firefox use webm/opus
      let mimeType = '';
      const formats = [
        'audio/mp4',           // Safari/iOS preferred
        'audio/aac',           // Safari fallback
        'audio/webm;codecs=opus', // Chrome preferred
        'audio/webm',          // Chrome fallback
        'audio/ogg;codecs=opus'  // Firefox
      ];
      
      for (const format of formats) {
        if (MediaRecorder.isTypeSupported(format)) {
          mimeType = format;
          break;
        }
      }
      
      console.log('Selected audio format:', mimeType || 'default');
      
      const options = mimeType ? { mimeType } : {};
      const mediaRecorder = new MediaRecorder(stream, options);
      mediaRecorderRef.current = mediaRecorder;
      chunksRef.current = [];

      // Determine file extension based on mime type
      const getExtension = (mime) => {
        if (mime.includes('mp4') || mime.includes('aac')) return 'mp4';
        if (mime.includes('ogg')) return 'ogg';
        return 'webm';
      };
      const fileExtension = getExtension(mimeType);

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          chunksRef.current.push(e.data);
          console.log('Audio chunk recorded:', e.data.size, 'bytes');
        }
      };

      mediaRecorder.onstop = () => {
        const actualMimeType = mimeType || 'audio/webm';
        const blob = new Blob(chunksRef.current, { type: actualMimeType });
        console.log('Recording stopped. Total size:', blob.size, 'bytes, type:', actualMimeType);
        
        // Create a file-like blob with proper name and extension
        const fileName = `recording.${fileExtension}`;
        const fileBlob = new File([blob], fileName, { type: actualMimeType });
        
        setAudioBlob(fileBlob);
        setAudioUrl(URL.createObjectURL(fileBlob));
        
        // Stop stream
        if (streamRef.current) {
          streamRef.current.getTracks().forEach(track => track.stop());
        }
      };

      mediaRecorder.start(100); // Collect data every 100ms
      setIsRecording(true);
      setRecordingTime(0);
      console.log('Recording started with', mimeType || 'default format');

      // Start timer
      timerRef.current = setInterval(() => {
        setRecordingTime(prev => {
          if (prev >= MAX_RECORDING_TIME) {
            stopRecording();
            return MAX_RECORDING_TIME;
          }
          return prev + 1;
        });
      }, 1000);
    } catch (error) {
      console.error('Microphone error:', error);
      if (error.name === 'NotAllowedError') {
        toast.error(ct('gim.errMicDenied'));
      } else if (error.name === 'NotFoundError') {
        toast.error(ct('gim.errMicNotFound'));
      } else if (error.name === 'NotReadableError') {
        toast.error(ct('gim.errMicBusy'));
      } else {
        toast.error(ct('gim.errMicGeneric'));
      }
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    }
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Check if it's an audio file
    if (!file.type.startsWith('audio/')) {
      toast.error(ct('gim.errNotAudio'));
      return;
    }

    // Check file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error(ct('gim.errTooBig'));
      return;
    }

    // Check duration
    const audio = document.createElement('audio');
    audio.preload = 'metadata';
    
    audio.onloadedmetadata = () => {
      window.URL.revokeObjectURL(audio.src);
      
      if (audio.duration > MAX_RECORDING_TIME) {
        toast.error(ct('gim.errTooLong', { n: MAX_RECORDING_TIME }));
        return;
      }

      // File is valid
      setAudioBlob(file);
      setAudioUrl(URL.createObjectURL(file));
      setRecordingTime(Math.floor(audio.duration));
      toast.success(ct('gim.okLoaded'));
    };

    audio.src = URL.createObjectURL(file);
  };

  const handleUpload = async () => {
    if (!audioBlob) {
      toast.error(ct('gim.errNoAudio'));
      return;
    }

    setUploading(true);

    try {
      const formData = new FormData();
      formData.append('audio_file', audioBlob, audioBlob.name || 'recording.webm');
      if (title) {
        formData.append('title', title);
      }
      if (replyTo?.id) {
        formData.append('reply_to_id', replyTo.id);
      }
      if (backgroundImage) {
        formData.append('background_image', backgroundImage);
      }

      await axios.post('/api/gim-toli', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      onSuccess();
    } catch (error) {
      toast.error(ct('gim.errUpload'));
      console.error('Upload error:', error);
    } finally {
      setUploading(false);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm overflow-hidden">
      <div 
        ref={modalRef}
        className="relative w-full max-w-lg bg-gray-900 rounded-3xl shadow-2xl border border-white/10 p-6 overflow-y-auto overscroll-contain transition-all duration-200"
        style={{
          maxHeight: `calc(100dvh - 2rem - ${keyboardHeight}px)`,
          paddingBottom: keyboardHeight > 0 ? `calc(${keyboardHeight}px + 2rem)` : '1.5rem',
          WebkitOverflowScrolling: 'touch',
        }}
      >
        {/* Close button */}
        <button
          data-testid="close-recorder-btn"
          onClick={onClose}
          className="absolute top-4 right-4 p-2 hover:bg-white/10 rounded-full transition-colors z-10"
        >
          <X className="w-5 h-5" />
        </button>

        <h2 className="text-2xl font-bold mb-4">{ct('gim.modalTitle')}</h2>

        {/* Reply indicator */}
        {replyTo && (
          <div className="mb-4 p-3 bg-violet-500/10 border border-violet-500/20 rounded-xl flex items-start gap-3">
            <div className="w-1 h-full min-h-[40px] bg-violet-500 rounded-full flex-shrink-0" />
            <div className="min-w-0">
              <p className="text-xs text-violet-400 font-medium">{ct('gim.replyTo', { name: replyTo.user?.name || ct('gim.replyToDefault') })}</p>
              <p className="text-sm text-gray-300 truncate">{replyTo.title || ct('gim.audio')}</p>
            </div>
          </div>
        )}

        {/* Mode Selection */}
        {!audioBlob && !isRecording && (
          <div className="flex gap-3 mb-6">
            <button
              onClick={() => setUploadMode('record')}
              className={`flex-1 py-3 px-4 rounded-xl font-semibold transition-all ${
                uploadMode === 'record'
                  ? 'bg-gradient-to-r from-magenta-500 to-violet-600 text-white'
                  : 'bg-white/10 text-gray-400 hover:bg-white/20'
              }`}
            >
              <Mic className="w-5 h-5 inline mr-2" />
              {ct('gim.tabRecord')}
            </button>
            <button
              onClick={() => setUploadMode('upload')}
              className={`flex-1 py-3 px-4 rounded-xl font-semibold transition-all ${
                uploadMode === 'upload'
                  ? 'bg-gradient-to-r from-magenta-500 to-violet-600 text-white'
                  : 'bg-white/10 text-gray-400 hover:bg-white/20'
              }`}
            >
              <Upload className="w-5 h-5 inline mr-2" />
              {ct('gim.tabUpload')}
            </button>
          </div>
        )}

        {/* Recording Interface */}
        <div className="space-y-6">
          {/* Upload Mode */}
          {uploadMode === 'upload' && !audioBlob && !isRecording && (
            <div className="text-center">
              <input
                ref={fileInputRef}
                type="file"
                accept="audio/*"
                onChange={handleFileUpload}
                className="hidden"
              />
              <button
                onClick={() => fileInputRef.current?.click()}
                className="w-full py-8 border-2 border-dashed border-white/20 rounded-2xl hover:border-magenta-500 hover:bg-white/5 transition-all"
              >
                <Upload className="w-12 h-12 mx-auto mb-3 text-magenta-500" />
                <p className="text-lg font-semibold mb-1">{ct('gim.pickAudioFile')}</p>
                <p className="text-sm text-gray-400">{ct('gim.audioLimits')}</p>
              </button>
            </div>
          )}

          {/* Timer */}
          {(isRecording || audioBlob) && (
            <div className="text-center">
            <div className={`text-5xl font-mono font-bold ${
              recordingTime >= 55 ? 'text-red-500' : 'text-white'
            }`}>
              {formatTime(recordingTime)} / 1:00
            </div>
            {isRecording && (
              <div className="flex items-center justify-center space-x-2 mt-3">
                <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse"></div>
                <span className="text-red-500 text-sm font-medium">{ct('gim.recording')}</span>
              </div>
            )}
          </div>

          )}

          {/* Record Button */}
          {uploadMode === 'record' && !audioBlob && (
            <div className="flex justify-center">
              <button
                data-testid={isRecording ? 'stop-recording-btn' : 'start-recording-btn'}
                onClick={isRecording ? stopRecording : startRecording}
                className={`w-20 h-20 rounded-full flex items-center justify-center transition-all ${
                  isRecording
                    ? 'bg-red-500 hover:bg-red-600'
                    : 'bg-gradient-to-r from-magenta-500 to-violet-600 hover:from-magenta-600 hover:to-violet-700'
                }`}
              >
                {isRecording ? (
                  <Square className="w-8 h-8" fill="white" />
                ) : (
                  <Mic className="w-8 h-8" />
                )}
              </button>
            </div>
          )}

          {/* Audio Preview and Publishing - For both modes */}
          {audioBlob && (
            <div className="w-full space-y-4 pb-4">
                {/* Audio player */}
                <audio
                  data-testid="recorded-audio-player"
                  src={audioUrl}
                  controls
                  className="w-full"
                />
                
                {/* Title input - with label always visible */}
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-300">
                    {t('messages.titleField')}
                  </label>
                  <input
                    ref={titleInputRef}
                    data-testid="post-title-input"
                    type="text"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder={ct('gim.titlePlaceholder')}
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl focus:outline-none focus:ring-2 focus:ring-magenta-500"
                  />
                </div>

                <MonetizationInfoBubble />

                {/* Optional background image */}
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-300 flex items-center gap-2">
                    <Upload className="w-4 h-4" />
                    {t('gimToli.background', ct('gim.background'))}
                  </label>
                  {backgroundPreview ? (
                    <div className="relative rounded-xl overflow-hidden border border-white/10" style={{ aspectRatio: '16/9' }} data-testid="gimtoli-bg-preview">
                      <img src={backgroundPreview} alt="bg" className="w-full h-full object-cover" />
                      <button
                        type="button"
                        onClick={() => {
                          setBackgroundImage(null);
                          if (backgroundPreview) URL.revokeObjectURL(backgroundPreview);
                          setBackgroundPreview(null);
                        }}
                        className="absolute top-2 right-2 p-1.5 rounded-full bg-black/70 hover:bg-red-500/70 text-white"
                        data-testid="gimtoli-bg-remove"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ) : (
                    <label className="block w-full py-6 rounded-xl border-2 border-dashed border-white/15 hover:border-magenta-400/50 hover:bg-white/5 transition-colors text-center cursor-pointer" data-testid="gimtoli-bg-picker">
                      <Upload className="w-5 h-5 mx-auto mb-1 text-white/50" />
                      <span className="text-xs text-white/60">{t('gimToli.backgroundPick', ct('gim.backgroundPick'))}</span>
                      <input
                        type="file"
                        accept="image/*"
                        hidden
                        data-testid="gimtoli-bg-input"
                        onChange={(e) => {
                          const f = e.target.files?.[0];
                          if (!f) return;
                          setBackgroundImage(f);
                          setBackgroundPreview(URL.createObjectURL(f));
                        }}
                      />
                    </label>
                  )}
                </div>

                {/* Audio effects panel */}
                {effects.fun.length > 0 && (
                  <div className="space-y-2" data-testid="audio-effects-panel">
                    <div className="flex items-center gap-2">
                      <Sparkles className="w-4 h-4 text-magenta-400" />
                      <label className="text-sm font-medium text-gray-300">{ct('gim.effectsLabel')}</label>
                      {previewing && <span className="text-xs text-magenta-300 animate-pulse">{ct('gim.effectsApplying')}</span>}
                    </div>
                    <div className="grid grid-cols-4 gap-2">
                      {effects.fun.map(p => (
                        <button
                          key={p.id}
                          type="button"
                          onClick={() => applyEffect(p)}
                          disabled={previewing}
                          data-testid={`effect-${p.id}`}
                          className={`p-2 rounded-xl border text-xs font-medium flex flex-col items-center gap-0.5 transition-all ${
                            selectedEffect?.id === p.id
                              ? 'bg-gradient-to-br from-magenta-500 to-violet-600 border-magenta-400 text-white shadow-md scale-105'
                              : 'bg-white/5 border-white/10 text-gray-200 hover:bg-white/10'
                          }`}
                        >
                          <span className="text-lg leading-none">{p.emoji}</span>
                          <span>{p.label}</span>
                        </button>
                      ))}
                    </div>
                    {effects.pro.length > 0 && (
                      <details className="mt-2">
                        <summary className="text-xs text-gray-400 cursor-pointer hover:text-magenta-400">
                          {ct('gim.proVoices')}
                        </summary>
                        <div className="grid grid-cols-3 gap-2 mt-2">
                          {effects.pro.map(p => (
                            <button
                              key={p.id}
                              type="button"
                              onClick={() => applyEffect(p)}
                              disabled={previewing}
                              data-testid={`pro-voice-${p.id}`}
                              className={`p-2 rounded-xl border text-xs font-medium flex flex-col items-center gap-0.5 transition-all ${
                                selectedEffect?.id === p.id
                                  ? 'bg-gradient-to-br from-cyan-500 to-blue-600 border-cyan-400 text-white shadow-md scale-105'
                                  : 'bg-white/5 border-white/10 text-gray-200 hover:bg-white/10'
                              }`}
                            >
                              <span className="text-lg leading-none">{p.emoji}</span>
                              <span className="truncate w-full">{p.label}</span>
                            </button>
                          ))}
                        </div>
                      </details>
                    )}
                  </div>
                )}

                {/* Action buttons */}
                <div className="flex space-x-3">
                  <button
                    data-testid="rerecord-btn"
                    onClick={() => {
                      setAudioBlob(null);
                      setAudioUrl(null);
                      setRecordingTime(0);
                      setTitle('');
                    }}
                    className="flex-1 py-3 bg-white/10 hover:bg-white/20 rounded-xl font-medium transition-colors"
                  >
                    {ct('gim.rerecord')}
                  </button>
                  <button
                    data-testid="upload-post-btn"
                    onClick={handleUpload}
                    disabled={uploading}
                    className="flex-1 py-3 bg-gradient-to-r from-magenta-500 to-violet-600 hover:from-magenta-600 hover:to-violet-700 rounded-xl font-medium transition-all disabled:opacity-50 flex items-center justify-center space-x-2"
                  >
                    <Upload className="w-5 h-5" />
                    <span>{uploading ? '...' : ct('gim.publish')}</span>
                  </button>
                </div>
            </div>
          )}

          {uploadMode === 'record' && !isRecording && !audioBlob && (
            <div className="space-y-2">
              <p className="text-center text-gray-400 text-sm">
                {ct('gim.tapToRecord')}
              </p>
              <div className="bg-blue-500/10 border border-blue-500/20 rounded-xl p-3">
                <p className="text-blue-400 text-xs text-center">
                  {ct('gim.micHint')}
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AudioRecorder;
